import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { api, ApiError, type Me } from '../../lib/api'
import { invaliderFiches } from '../../lib/cache'
import { intentionDuCode } from './lienDuCode'
import { layout } from '../../design/tokens'
import { ChampCode } from './ChampCode'

/**
 * Créer ou changer son code confidentiel — celui qui ouvre les fiches protégées.
 *
 * Une page et non une feuille : on y arrive en voulant protéger une fiche, et il faut pouvoir
 * en repartir vers elle. Le chemin de retour voyage dans l'adresse (`?retour=`), de sorte qu'un
 * rafraîchissement en cours de route ne le perde pas.
 *
 * Ce code n'est pas le mot de passe du compte. Quatre chiffres ne protègent pas une session —
 * ils protègent une fiche, et le serveur met la saisie en pause au bout de cinq erreurs. Les
 * confondre aurait fait d'un secret court la clé de tout.
 */
export function CodeConfidentielPage() {
  const [parametres] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: me, isPending } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me })

  const [actuel, setActuel] = useState('')
  const [nouveau, setNouveau] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  // Le code est posé : un second essai ne doit pas le redéfinir, il n'a plus que la suite à
  // reprendre. Sans ce témoin, réessayer après un échec du verrou renvoyait un code sans
  // l'ancien, que le serveur refuse désormais à bon droit.
  const [codePose, setCodePose] = useState(false)

  // Le retour sûr et la fiche qu'on venait protéger, relus par la fonction qui a écrit le lien :
  // les deux sens vivent dans `lienDuCode.ts`, et un test prouve qu'ils se répondent.
  const { retour, aProteger } = intentionDuCode(parametres)

  const discordant = confirmation.length === 4 && confirmation !== nouveau

  const enregistrer = useMutation({
    mutationFn: async () => {
      if (!codePose) {
        await api.setPin(nouveau, me?.hasPin ? actuel : undefined)
        setCodePose(true)
      }

      if (aProteger) {
        await api.lockItem(aProteger.espace, aProteger.fiche)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] })

      if (aProteger) {
        // Oubliée du cache, et non simplement marquée à relire : la copie en mémoire est encore
        // la fiche **ouverte**, et React Query la rendrait le temps d'un aller-retour. On
        // verrait son contenu une fraction de seconde après l'avoir protégée.
        queryClient.removeQueries({ queryKey: ['fiche', aProteger.espace, aProteger.fiche] })
        await invaliderFiches(queryClient, aProteger.espace)
      }

      void navigate(retour ?? '/', { replace: true })
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'enregistrement a échoué."),
  })

  if (isPending || !me) {
    return (
      <main
        className="mx-auto min-h-dvh bg-surface"
        style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
      >
        <div className="mt-16 rounded-field bg-sunken" style={{ height: 20, width: '60%' }} />
      </main>
    )
  }

  const complet =
    nouveau.length === 4 && confirmation === nouveau && (!me.hasPin || actuel.length === 4)

  return (
    <main
      className="mx-auto min-h-dvh bg-surface pb-12"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
    >
      <header className="flex items-center" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="rounded-button border border-border bg-card"
          style={{ width: 44, height: 44 }}
        >
          ←
        </button>
      </header>

      <h1 className="font-serif text-screen">
        {me.hasPin ? 'Changer mon code' : 'Créer mon code'}
      </h1>

      {/* Dire la suite dès le départ : on est arrivé ici en voulant protéger une fiche, pas en
          voulant créer un code, et l'écran doit confirmer qu'il se souvient du but. */}
      {aProteger ? (
        <p className="mt-3 text-body font-semibold text-accent">
          La fiche sera protégée dès le code enregistré.
        </p>
      ) : null}

      <p className="mt-3 text-body text-ink-muted" style={{ maxWidth: '60ch' }}>
        {me.hasPin
          ? 'Les fiches déjà protégées s’ouvriront avec le nouveau code.'
          : 'Quatre chiffres, à vous. Ils ouvriront les fiches que vous protégez, et celles qu’on vous aura confiées.'}
      </p>

      {/* Dit avant la saisie, pas après : il n'existe aucune récupération, et c'est la seule
          chose qui change la façon de choisir son code. */}
      <p className="mt-3 rounded-card border border-border bg-sunken p-3 text-meta">
        Ce code ne peut pas être retrouvé. Oublié, le contenu des fiches protégées n’est plus
        lisible — seule l’administration peut encore y accéder.
      </p>

      {me.hasPin ? (
        <section className="mt-6">
          <ChampCode
            libelle="Code actuel"
            valeur={actuel}
            onChange={(valeur) => {
              setActuel(valeur)
              setErreur(null)
            }}
            autoFocus
          />
        </section>
      ) : null}

      <section className="mt-6">
        <ChampCode
          libelle={me.hasPin ? 'Nouveau code' : 'Votre code'}
          valeur={nouveau}
          onChange={(valeur) => {
            setNouveau(valeur)
            setErreur(null)
          }}
          autoFocus={!me.hasPin}
        />
      </section>

      <section className="mt-4">
        <ChampCode
          libelle="Retapez-le"
          valeur={confirmation}
          onChange={(valeur) => {
            setConfirmation(valeur)
            setErreur(null)
          }}
          onEntree={() => complet && enregistrer.mutate()}
          enErreur={discordant}
        />
        {discordant ? (
          <p className="mt-2 text-meta" style={{ color: 'var(--color-danger)' }}>
            Les deux saisies diffèrent.
          </p>
        ) : null}
      </section>

      {erreur ? (
        <p className="mt-4 text-meta" style={{ color: 'var(--color-danger)' }}>
          {erreur}
        </p>
      ) : null}

      <button
        type="button"
        disabled={!complet || enregistrer.isPending}
        onClick={() => enregistrer.mutate()}
        className="mt-6 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {enregistrer.isPending
          ? 'Enregistrement…'
          : aProteger
            ? 'Enregistrer et protéger la fiche'
            : 'Enregistrer'}
      </button>
    </main>
  )
}
